/**
 * Google Consent Mode v2 for the site's GA4 tag.
 * Defaults (set in the root head script): denied in the EEA, UK and Switzerland
 * until the visitor accepts; granted elsewhere unless the visitor declines.
 * The stored record keeps the choice, when it was made and the notice version.
 */
export const CONSENT_STORAGE_KEY = "drsara:consent:v1";
export const CONSENT_NOTICE_VERSION = "2026-10";
export const OPEN_CONSENT_EVENT = "drsara:open-consent";

export const CONSENT_REGIONS = [
  "AT", "BE", "BG", "HR", "CY", "CZ", "DK", "EE", "FI", "FR", "DE", "GR", "HU", "IE", "IT",
  "LV", "LT", "LU", "MT", "NL", "PL", "PT", "RO", "SK", "SI", "ES", "SE", "IS", "LI", "NO",
  "GB", "CH",
] as const;

export type ConsentChoice = "granted" | "denied";
export type ConsentRecord = { choice: ConsentChoice; at: string; version: string; region: string | null };

/** Inline head script: defaults + re-apply a stored choice before GA config runs. */
export function consentBootScript(measurementId: string): string {
  const regions = JSON.stringify(CONSENT_REGIONS);
  return `window.dataLayer = window.dataLayer || [];
function gtag(){dataLayer.push(arguments);}
var d={ad_storage:'denied',analytics_storage:'denied',ad_user_data:'denied',ad_personalization:'denied'};
gtag('consent','default',Object.assign({region:${regions},wait_for_update:500},d));
gtag('consent','default',{ad_storage:'granted',analytics_storage:'granted',ad_user_data:'granted',ad_personalization:'granted'});
try{var r=JSON.parse(localStorage.getItem('${CONSENT_STORAGE_KEY}')||'null');if(r&&(r.choice==='granted'||r.choice==='denied')){var v=r.choice;gtag('consent','update',{ad_storage:v,analytics_storage:v,ad_user_data:v,ad_personalization:v});}}catch(e){}
gtag('js', new Date());
gtag('config', '${measurementId}');`;
}

export function readConsent(): ConsentRecord | null {
  if (typeof window === "undefined") return null;
  try {
    const r = JSON.parse(window.localStorage.getItem(CONSENT_STORAGE_KEY) ?? "null");
    return r && (r.choice === "granted" || r.choice === "denied") ? (r as ConsentRecord) : null;
  } catch {
    return null;
  }
}

export function applyConsent(choice: ConsentChoice) {
  const g = (window as unknown as { gtag?: (...a: unknown[]) => void }).gtag;
  g?.("consent", "update", {
    ad_storage: choice,
    analytics_storage: choice,
    ad_user_data: choice,
    ad_personalization: choice,
  });
}

export function saveConsent(choice: ConsentChoice, region: string | null) {
  const record: ConsentRecord = { choice, at: new Date().toISOString(), version: CONSENT_NOTICE_VERSION, region };
  try {
    window.localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(record));
  } catch {
    /* storage blocked: the choice still applies for this page */
  }
  applyConsent(choice);
}

/** Country from same-origin /cdn-cgi/trace; null when unknown (treated as a consent region). */
export async function detectCountry(): Promise<string | null> {
  try {
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), 2000);
    const res = await fetch("/cdn-cgi/trace", { signal: ctrl.signal });
    clearTimeout(t);
    if (!res.ok) return null;
    const loc = /(?:^|\n)loc=([A-Z0-9]{2})/.exec(await res.text())?.[1] ?? null;
    return loc && loc !== "XX" && loc !== "T1" ? loc : null;
  } catch {
    return null;
  }
}

export function needsBanner(country: string | null): boolean {
  return country === null || (CONSENT_REGIONS as readonly string[]).includes(country);
}

export function openConsentSettings() {
  window.dispatchEvent(new Event(OPEN_CONSENT_EVENT));
}
